import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../api.js";
import { formatFileSize, onboardingStep } from "../onboarding-model.js";
import { CenteredCard } from "./RestaurantPicker.js";
import { logout } from "../auth.js";
import { ErrorPage, errorDetails } from "../error-page.js";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  FileIcon,
  Hint,
  MailIcon,
  SendIcon,
  SignOutIcon,
  TrashIcon,
  WithIcon
} from "../icons.js";

/**
 * Onboarding: the step between signing up and having a restaurant.
 *
 * Progress is saved on the server rather than held in this component, so closing the tab and
 * coming back resumes where they left off — the reason the application has a DRAFT state at all.
 */
export function OnboardingPage() {
  return (
    <>
      <SignOutCorner>
        <BackToLocations />
      </SignOutCorner>
      <OnboardingContent />
    </>
  );
}

/**
 * Sign out, on every state of this page: loading, unavailable, the form and "you're all set".
 *
 * This page sits outside the restaurant layout, whose header is the only other place with a sign-out button,
 * so a new owner here (or someone who signed up with the wrong address) had no way out. Pinned to the corner
 * rather than added to the card, because the card is shared with the signed-out sign-in screen.
 */
export function SignOutCorner({ children }: { children?: React.ReactNode } = {}) {
  return (
    <nav className="signout-corner" aria-label="Account">
      {children}
      <button className="ghost with-icon" onClick={() => void logout()}>
        <SignOutIcon size={16} />
        <span>Sign out</span>
      </button>
    </nav>
  );
}

/**
 * Back to the location picker, for someone adding ANOTHER restaurant. They reached this page with "Add a
 * restaurant" and may change their mind. A first-time owner has no locations to go back to, so it's hidden.
 */
function BackToLocations() {
  const mine = useQuery({ queryKey: ["restaurants"], queryFn: () => api.listRestaurants(), retry: false });
  if (!mine.data?.restaurants.length) return null;
  return (
    <Link to="/" className="button-link ghost with-icon">
      <ArrowLeftIcon size={16} />
      <span>Your locations</span>
    </Link>
  );
}

function OnboardingContent() {

  // POST rather than GET on mount: this both reads and creates-if-absent, so a freshly signed-up
  // owner lands on a usable form instead of an empty state telling them to do something.
  const application = useQuery({
    queryKey: ["application"],
    queryFn: api.startOrResumeApplication,
    retry: false
  });

  const step = onboardingStep(application.data);

  if (application.isPending) return <CenteredCard>Loading…</CenteredCard>;

  if (application.error) {
    return <ErrorPage details={errorDetails(application.error, { context: "onboarding: load application" })} />;
  }

  if (step === "submitted") {
    return <SubmittedCard nextStep={application.data?.nextStep} />;
  }

  return <ApplicationForm />;
}

function SubmittedCard({ nextStep }: { nextStep?: string }) {
  return (
    <CenteredCard>
      <h1>
        <WithIcon icon={<CheckCircleIcon size={22} />}>You’re all set</WithIcon>
      </h1>
      <p className="muted">{nextStep ?? "We’ll be in touch shortly."}</p>
      <p className="muted">
        <WithIcon icon={<MailIcon size={16} />}>We’ll email you when you’re ready to take calls.</WithIcon>
        <Hint>A person sets up your phone number and checks your menu, so this part isn’t instant.</Hint>
      </p>
    </CenteredCard>
  );
}

/**
 * The whole form: the menu. Its name, address and hours are almost always on it, and setup reads them from there,
 * so asking for them again was work for the owner that we redid. Submit is enabled once one file is attached.
 */
function ApplicationForm() {
  const queryClient = useQueryClient();
  const [problems, setProblems] = useState<string[]>([]);
  const uploads = useQuery({ queryKey: ["application-uploads"], queryFn: api.listApplicationUploads, retry: false });
  const hasMenu = (uploads.data?.files.length ?? 0) > 0;

  const submit = useMutation({
    mutationFn: () => api.submitApplication(),
    onSuccess: () => {
      setProblems([]);
      void queryClient.invalidateQueries({ queryKey: ["application"] });
    },
    onError: (err: unknown) =>
      setProblems(
        err instanceof ApiError
          ? // The one thing the server can refuse for, in the owner's words rather than the API's.
            (err.problems.length > 0 ? err.problems : [err.message]).map((p) =>
              /menu is required/i.test(p) ? "Add your menu first." : p
            )
          : ["Could not submit. Please try again."]
      )
  });

  return (
    <CenteredCard>
      <h1>
        Send us your menu
        <Hint>We set up your phone agent from it: the dishes, prices, and usually your name, address and hours.</Hint>
      </h1>
      <p className="muted">A PDF or photos of each page. We’ll take it from there.</p>

      <MenuUploads />

      {problems.map((problem) => (
        <p className="error" key={problem}>
          {problem}
        </p>
      ))}

      <button className="primary wide" disabled={!hasMenu || submit.isPending} onClick={() => submit.mutate()}>
        <WithIcon icon={<SendIcon size={16} />}>{submit.isPending ? "Submitting…" : "Submit"}</WithIcon>
      </button>
      {!hasMenu ? <p className="muted small">Add at least one file to submit.</p> : null}
    </CenteredCard>
  );
}

function MenuUploads() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  const uploads = useQuery({
    queryKey: ["application-uploads"],
    queryFn: api.listApplicationUploads,
    retry: false
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["application-uploads"] });

  const add = useMutation({
    mutationFn: async (files: FileList) => {
      // Sequential, not parallel: each presign counts existing files against the per-application
      // cap, so concurrent requests would all see the old count and overshoot it.
      for (const file of Array.from(files)) await api.uploadMenuFile(file);
    },
    onSuccess: () => {
      setError(undefined);
      refresh();
    },
    onError: (err: unknown) => {
      // The server's messages are written for the applicant ("larger than 15MB", "PDF or a photo"),
      // so they are shown as-is rather than replaced with something vaguer.
      setError(err instanceof ApiError ? err.message : "That file couldn’t be uploaded");
      refresh(); // an earlier file in the batch may have succeeded
    }
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => api.deleteApplicationUpload(fileId),
    onSuccess: refresh
  });

  const files = uploads.data?.files ?? [];

  return (
    <div className="field">

      <input
        ref={inputRef}
        type="file"
        aria-label="Add menu files"
        multiple
        accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={(e) => {
          const chosen = e.target.files;
          if (chosen && chosen.length > 0) add.mutate(chosen);
          // Clear the input so choosing the same file twice still fires a change event.
          e.target.value = "";
        }}
        disabled={add.isPending}
      />
      {add.isPending ? <span className="muted">Uploading…</span> : null}
      {error ? <span className="error">{error}</span> : null}

      {files.length > 0 ? (
        <ul className="upload-list">
          {files.map((file) => (
            <li key={file.fileId}>
              <WithIcon icon={<FileIcon size={15} />}>{file.filename}</WithIcon>
              <span className="muted"> {formatFileSize(file.sizeBytes)}</span>
              <button
                className="link"
                onClick={() => remove.mutate(file.fileId)}
                disabled={remove.isPending}
                aria-label={`Remove ${file.filename}`}
                title="Remove"
              >
                <TrashIcon size={15} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
